'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Text, Picture, Heading, Container, Link } from '../../components/index.js'
import { cva } from 'class-variance-authority'
import type { ActivityScheduleProps, ActivityScheduleItem } from './types.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import { withLayout } from '../../shared/Styles.js'

/**
 * Card style variants for activity schedule
 */
const activityCardVariants = cva(
  'rounded-card laptop:px-6 laptop:py-4 relative flex min-w-0 flex-col justify-between gap-2 overflow-hidden px-4 py-3',
  {
    variants: {
      state: {
        // Active state - gradient background
        'active-light': 'bg-gradient-to-r from-[#3555aa] to-[#6988e3]',
        'active-dark': 'bg-gradient-to-r from-[#3555aa] to-[#6988e3]',
        // Inactive state - solid background
        'inactive-light': 'bg-[#EAEAEC]',
        'inactive-dark': 'bg-[#1E2024]',
      },
      size: {
        // Small size - 120px height with responsive scaling
        small: 'laptop:h-[160px] h-[120px]',
        // Large size - 200px height with responsive scaling
        large: 'laptop:h-[240px] h-[200px]',
      },
    },
    defaultVariants: {
      state: 'inactive-light',
      size: 'small',
    },
  }
)

/**
 * Text style variants for card
 */
const cardTextVariants = cva('font-bold leading-[1.2]', {
  variants: {
    state: {
      // Active state - white text
      'active-light': 'text-white',
      'active-dark': 'text-white',
      // Inactive state - dark text
      'inactive-light': 'text-[#080A0F]',
      'inactive-dark': 'text-white',
    },
  },
  defaultVariants: {
    state: 'inactive-light',
  },
})

/**
 * Activity Schedule Card Component
 */
const ActivityScheduleCard = React.memo(
  ({
    item,
    theme = 'light',
    className,
    size,
  }: {
    item: ActivityScheduleItem
    theme?: 'light' | 'dark'
    className?: string
    size: 'small' | 'large'
  }) => {
    const isActive = item.isActive || false

    // Calculate combined state
    const cardState = `${isActive ? 'active' : 'inactive'}-${theme}` as
      | 'active-light'
      | 'active-dark'
      | 'inactive-light'
      | 'inactive-dark'

    return (
      <div
        className={cn(activityCardVariants({ state: cardState, size }), className)}
        data-ui-component-id="ActivityScheduleCard"
      >
        <Link asChild={!item?.link} href={item?.link} className="size-full no-underline hover:text-current">
          {/* Background decoration icon - show activeIcon when active, inactiveIcon when inactive */}
          {(isActive ? item.activeIcon : item.inactiveIcon) && (
            <div className="laptop:w-[160px] aspect-1 absolute right-0 top-0 z-10 w-[120px]">
              <Picture
                source={isActive ? item.activeIcon!.url : item.inactiveIcon!.url}
                alt={isActive ? item.activeIcon!.alt : item.inactiveIcon!.alt}
                className="aspect-square"
              />
            </div>
          )}

          {/* Content area */}
          <div className="relative z-20 flex h-full flex-col justify-between">
            {/* Title */}
            <Heading as="h3" size={3} html={item.title} className={cn(cardTextVariants({ state: cardState }))} />

            <div className="flex flex-col gap-1">
              {/* Subtitle */}
              <div className="flex flex-col justify-end overflow-hidden">
                <Heading
                  as="h4"
                  size={2}
                  html={item.subtitle}
                  className={cn(' line-clamp-2', cardTextVariants({ state: cardState }))}
                />
              </div>
              {/* Detailed description - only show when size is large */}
              {size === 'large' && item.description && (
                <Text
                  html={item.description}
                  className={cn(
                    'lg-desktop:text-[18px] desktop:text-[16px] line-clamp-3 text-[14px] leading-[1.4]',
                    cardTextVariants({ state: cardState })
                  )}
                />
              )}
            </div>
          </div>
        </Link>
      </div>
    )
  }
)

ActivityScheduleCard.displayName = 'ActivityScheduleCard'

/**
 * ActivitySchedule - Activity Schedule Component
 *
 * @description Display activity schedule card list with active/inactive states, theme switching, and responsive layout
 */
const ActivitySchedule = React.forwardRef<HTMLDivElement, ActivityScheduleProps>(
  ({ classNames = {}, data, ...rest }, ref) => {
    const theme = data.theme || 'light'
    const size = data.size || 'large'

    const itemsPerRow = React.useMemo(() => {
      return data?.scheduleList?.length || 2
    }, [data?.scheduleList])

    const swiperBreakpoints = React.useMemo(() => {
      const getBreakpointConfig = (breakpoint: 'mobile' | 'tablet' | 'laptop' | 'desktop') => {
        switch (breakpoint) {
          case 'mobile':
            return { slidesPerView: 1.17, spaceBetween: 12 }
          case 'tablet':
            if (itemsPerRow === 2) return { slidesPerView: 2, spaceBetween: 12 }
            return { slidesPerView: 2.4, spaceBetween: 12 }
          case 'laptop':
            if (itemsPerRow === 2) return {}
            if (itemsPerRow === 4) return { slidesPerView: 3.2, spaceBetween: 16 }
            return { slidesPerView: itemsPerRow, spaceBetween: 16 }
          case 'desktop':
            if (itemsPerRow === 2) return { slidesPerView: 2, spaceBetween: 16 }
            return { slidesPerView: itemsPerRow, spaceBetween: 16 }
        }
      }

      const mobileConfig = getBreakpointConfig('mobile')
      const tabletConfig = getBreakpointConfig('tablet')
      const laptopConfig = getBreakpointConfig('laptop')
      const desktopConfig = getBreakpointConfig('desktop')

      return {
        0: mobileConfig,
        768: tabletConfig,
        1024: laptopConfig,
        1440: desktopConfig,
      }
    }, [itemsPerRow])

    return (
      <Container
        ref={ref}
        className={cn(classNames?.root)}
        childClassName="overflow-hidden"
        data-ui-component-id="ActivitySchedule"
        {...rest}
      >
        <Swiper breakpoints={swiperBreakpoints} className="w-full !overflow-visible">
          {data.scheduleList.map((item, index) => (
            <SwiperSlide key={'activityScheduleCardItem' + index}>
              <ActivityScheduleCard item={item} theme={theme} size={size} className={classNames?.card} />
            </SwiperSlide>
          ))}
        </Swiper>
      </Container>
    )
  }
)

ActivitySchedule.displayName = 'ActivitySchedule'

export default withLayout(ActivitySchedule)
export type { ActivityScheduleProps, ActivityScheduleData, ActivityScheduleItem } from './types.js'
